import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useToast } from '@/hooks/use-toast';
import { X, Globe } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { selectAllFolders } from '@/store/slices/folder/folderSelectors';
import { EnhancedTagsInput } from '../shared/EnhancedTagsInput';
import { EnhancedChatbotInput } from '../shared/EnhancedChatbotInput';
import { FormSection } from '../shared/FormSection';
import { Switch } from '@/components/ui/switch';
import { Folder } from 'lucide-react';
import { createWebsite } from '@/store/slices/content/contentSlice';
import type { Content } from '@/types/content';
import { ContentStatus } from '@/types/content';
import { selectTags, fetchTags } from '@/store/slices/tagsSlice';
import { selectChatbots, fetchChatbotsIfNeeded } from '@/store/slices/chatbots/chatbotsSlice';
import { selectAllProfiles, fetchCopilotProfiles } from '@/store/slices/copilot-profiles/copilotProfilesSlice';
import { EnhancedCopilotInput } from '../shared/EnhancedCopilotInput';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface ScrapeWebsiteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ScrapeWebsiteModal: React.FC<ScrapeWebsiteModalProps> = ({
  open,
  onOpenChange
}) => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isLive, setIsLive] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [linkedChatbots, setLinkedChatbots] = useState<string[]>([]);
  const [linkedCopilots, setLinkedCopilots] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>('all');
  const [isValidUrl, setIsValidUrl] = useState(true);
  
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const folders = useAppSelector(selectAllFolders);
  const availableTags = useAppSelector(selectTags);
  const availableChatbots = useAppSelector(selectChatbots).map(chatbot => ({
    id: chatbot.id,
    name: chatbot.name
  }));
  const availableCopilots = useAppSelector(selectAllProfiles).map(copilot => ({
    id: copilot.id,
    name: copilot.name
  }));

  // Get available folders for selection
  const availableFolders = [
    { id: 'all', name: 'All Content' },
    ...folders.filter(folder => folder.id !== 'all')
  ];

  const validateUrl = (input: string) => {
    try {
      new URL(input);
      return true;
    } catch {
      return false;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUrl(value);
    
    if (value && !validateUrl(value)) {
      setIsValidUrl(false);
    } else {
      setIsValidUrl(true);
    }
  };

  const resetForm = () => {
    setUrl('');
    setTitle('');
    setDescription('');
    setTags([]);
    setIsLive(false);
    setLinkedCopilots([]);
    setLinkedChatbots([]);
    setSelectedFolderId('all');
    setIsValidUrl(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url || !isValidUrl) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid website URL",
        variant: "destructive"
      });
      return;
    }

    if (!title.trim()) {
      toast({
        title: "Missing title",
        description: "Please enter a title",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const contentData: Content = {
        title: title || `Scraped from ${url}`,
        content: `${url}`,
        type: 'website' as const,
        status: 'processing' as ContentStatus,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        description: `Content scraped from ${url}`,
        folderId: selectedFolderId === 'all' ? undefined : selectedFolderId,
        tags: tags.length > 0 ? tags : undefined,
        chatbots: linkedChatbots.length > 0 ? linkedChatbots : undefined,
        copilots: linkedCopilots.length > 0 ? linkedCopilots : undefined,
      };

      await dispatch(createWebsite(contentData)).unwrap();

      toast({
        title: "Website ingestion started",
        description: "We'll notify you once processing finishes."
      });

      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error scraping website",
        description: "An error occurred while scraping the website",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch tags and chatbots when modal opens
  useEffect(() => {
    if (open) {
      dispatch(fetchTags({
        searchQuery: '',
        filterEntity: 'all',
        sortField: 'name',
        sortDirection: 'asc',
        page: 1,
        limit: 1000
      }));
      dispatch(fetchChatbotsIfNeeded());
      dispatch(fetchCopilotProfiles());
    }
  }, [open, dispatch]);

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) {
        if (isSubmitting) {
          return;
        }
        resetForm();
      }
      onOpenChange(newOpen);
    }}>
      <DialogContent className="sm:max-w-md bg-white dark:bg-gray-950 rounded-xl shadow-lg border border-gray-100 dark:border-gray-800 max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <Globe className="h-5 w-5 text-primary" />
            Scrape Website
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={() => !isSubmitting && onOpenChange(false)}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Title field */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-medium">Title</Label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a title"
              className="border-gray-200 dark:border-gray-800 focus:ring-primary/30 focus:border-primary"
              disabled={isSubmitting}
              required
            />
          </div>
          
          {/* Description field */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a brief description"
              className="h-20 resize-none border-gray-200 dark:border-gray-800 focus:ring-primary/30 focus:border-primary"
              disabled={isSubmitting}
            />
          </div>

          {/* URL field */}
          <div className="space-y-2">
            <Label htmlFor="url" className="text-sm font-medium">Website URL <span className="text-destructive">*</span></Label>
            <div className="relative">
              <Globe className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input 
                id="url" 
                value={url} 
                onChange={handleUrlChange} 
                placeholder="https://example.com"
                className="pl-9 border-gray-200 dark:border-gray-800 focus:ring-primary/30 focus:border-primary"
                required
                disabled={isSubmitting}
              />
            </div>
            {!isValidUrl && url && (
              <p className="text-sm text-destructive">Please enter a valid URL (including https://)</p>
            )}
          </div>

          {/* Folder selection */}
          <div className="space-y-2">
            <Label htmlFor="folder" className="text-sm font-medium">Folder</Label>
            <Select 
              value={selectedFolderId || 'all'} 
              onValueChange={setSelectedFolderId}
              disabled={isSubmitting}
            >
              <SelectTrigger className="w-full border-gray-200 dark:border-gray-800 focus:ring-primary/30 focus:border-primary">
                <SelectValue placeholder="Select folder" />
              </SelectTrigger>
              <SelectContent>
                {availableFolders.map((folder) => (
                  <SelectItem key={folder.id} value={folder.id}>
                    {folder.id === 'all' ? (
                      folder.name
                    ) : (
                      <div className="flex items-center gap-2">
                        <Folder className="h-4 w-4 text-muted-foreground" />
                        <span>{folder.name}</span>
                      </div>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tags selection */}
          <div className="space-y-2">
            <Label htmlFor="tags" className="text-sm font-medium">Tags</Label>
            <EnhancedTagsInput
              tags={tags}
              setTags={setTags}
              availableTags={availableTags}
              disabled={isSubmitting}
              maxDisplayCount={4}
            />
          </div>

          {/* Advanced Options Section */}
          <FormSection title="Advanced Options" defaultExpanded={false}>
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="visibility" className="text-sm font-medium">Visibility</Label>
                  <p className="text-xs text-muted-foreground">Make content live after processing</p>
                </div>
                <Switch
                  id="visibility"
                  checked={isLive}
                  onCheckedChange={setIsLive}
                  disabled={isSubmitting}
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="linkedCopilots" className="text-sm font-medium">Linked Copilot Profiles</Label>
                <EnhancedCopilotInput
                  copilots={linkedCopilots}
                  setCopilots={setLinkedCopilots}
                  availableCopilots={availableCopilots}
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedChatbots" className="text-sm font-medium">Linked Chatbot Profiles</Label>
                <EnhancedChatbotInput
                  chatbots={linkedChatbots}
                  setChatbots={setLinkedChatbots}
                  availableChatbots={availableChatbots}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </FormSection>

          <div className="bg-muted p-3 rounded-md mt-2">
            <h4 className="font-medium text-sm">What will be scraped</h4>
            <ul className="list-disc list-inside mt-1 text-xs text-muted-foreground space-y-1">
              <li>Main page content</li>
              <li>FAQ sections</li>
              <li>Product information</li>
              <li>Public documentation</li>
            </ul>
          </div>
          
          <DialogFooter className="pt-2">
            <Button 
              variant="outline" 
              type="button" 
              onClick={() => onOpenChange(false)} 
              disabled={isSubmitting}
              className="h-9 border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || !isValidUrl || !url.trim()}
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-9"
            >
              {isSubmitting ? 'Starting...' : 'Start Scraping'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
